import { OrderItem } from '../types/products';
import { Visionary } from '../types/visionary';
import { sendOrderConfirmationSMS } from './vonage';
import { sendOrderConfirmationEmail } from './email';

// Centralized toast notification system
export const showToast = (
  message: string, 
  type: 'success' | 'info' | 'warning' | 'error' = 'success',
  duration: number = 3000
): void => {
  const colors = {
    success: 'from-green-500 to-emerald-500 border-green-400/30',
    info: 'from-blue-500 to-indigo-500 border-blue-400/30',
    warning: 'from-orange-500 to-amber-500 border-orange-400/30',
    error: 'from-red-500 to-rose-500 border-red-400/30'
  };

  const icons = {
    success: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>',
    info: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>',
    warning: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"></path>',
    error: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>'
  };

  const notification = document.createElement('div');
  notification.className = `
    fixed top-4 right-4 z-50 max-w-sm p-4 
    bg-gradient-to-r ${colors[type]}
    text-white rounded-lg shadow-2xl border
    transform translate-x-full opacity-0 transition-all duration-300
    backdrop-blur-xl
  `;
  
  notification.innerHTML = `
    <div class="flex items-center gap-3">
      <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          ${icons[type]}
        </svg>
      </div>
      <div class="text-sm leading-relaxed">${message}</div>
    </div>
  `;

  document.body.appendChild(notification);

  // Animate in
  setTimeout(() => {
    notification.style.transform = 'translateX(0)';
    notification.style.opacity = '1';
  }, 100);

  // Remove after duration
  setTimeout(() => {
    notification.style.transform = 'translateX(100%)';
    notification.style.opacity = '0';
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 300);
  }, duration);
};

/**
 * Sends order confirmation notifications (SMS and Email)
 * @param orderId - Order/Invoice ID
 * @param totalAmount - Total amount of the order
 * @param orderItems - Items included in the order
 * @param visionary - Customer information (if applicable)
 * @param staffName - Name of staff who processed the order (if applicable)
 * @param staffId - ID of staff member who processed the order (if applicable)
 * @param pointsUsed - Loyalty points used
 * @param pointsEarned - Loyalty points earned
 */
export const sendOrderConfirmations = async (
  orderId: string,
  totalAmount: number,
  orderItems: OrderItem[],
  visionary: Visionary | null = null,
  staffName?: string,
  staffId?: number,
  pointsUsed: number = 0,
  pointsEarned: number = 0
): Promise<void> => {
  const itemCount = orderItems.length;

  try {
    // Send SMS notification
    await sendOrderConfirmationSMS(
      orderId,
      totalAmount,
      itemCount
    ).catch(error => {
      console.error('SMS notification failed:', error);
    });

    // Send email notification with PDF invoice
    await sendOrderConfirmationEmail(
      orderId,
      totalAmount,
      itemCount,
      orderItems,
      visionary,
      staffName,
      staffId,
      pointsUsed,
      pointsEarned
    ).catch(error => {
      console.error('Email notification failed:', error);
    });
  } catch (error) {
    console.error('Error sending notifications:', error);
  }
}; 